import {
  Container,
  Heading,
  Text,
  VStack,
  SimpleGrid,
  Box,
  Icon,
  List,
  ListItem,
  HStack,
  useColorModeValue,
  Card,
  CardBody,
  CardHeader,
  Flex,
  Badge,
  Divider,
} from '@chakra-ui/react';
import {
  MapPin,
  Camera,
  Users,
  Database,
  GlobeHemisphereWest,
  MagnifyingGlass,
  Stack as StackIcon, // Alias Stack to avoid conflict
  Compass,
  Lightbulb,
  Star,
} from '@phosphor-icons/react';

export const FeaturesSection = () => {
  // Use theme tokens directly
  const headingColor = useColorModeValue('interactive.primary', 'interactive.primary');
  const textColor = useColorModeValue('text.default', 'text.default');
  const textColorMuted = useColorModeValue('text.muted', 'text.muted');
  const accentBorderColor = useColorModeValue('interactive.primary', 'interactive.primary');

  const features = [
    {
      icon: MapPin,
      iconColor: useColorModeValue('interactive.primary', 'interactive.primary'),
      title: "Global Coverage",
      description: "Explore megalithic sites worldwide with precise GPS coordinates and interactive mapping"
    },
    {
      icon: MagnifyingGlass,
      iconColor: useColorModeValue('brand.secondary.500', 'brand.secondary.300'),
      title: "Advanced Analysis",
      description: "AI-powered feature detection identifies key characteristics with our standardized classification system"
    },
    {
      icon: StackIcon,
      iconColor: useColorModeValue('accent.gold', 'accent.gold'),
      title: "Comprehensive Data",
      description: "Access detailed profiles including construction techniques, material analysis, and astronomical alignments"
    },
    {
      icon: GlobeHemisphereWest,
      iconColor: useColorModeValue('brand.secondary.500', 'brand.secondary.300'),
      title: "Community Driven",
      description: "Join researchers and enthusiasts to contribute discoveries, validate findings, and collaborate on research"
    }
  ];

  const platformFeatures = [
    { icon: MapPin, text: "Interactive World Map: Discover megalithic sites across the globe." },
    { icon: Database, text: "Detailed Site Profiles: Explore in-depth information, images, and discussions." },
    { icon: Compass, text: "Sub-Location Mapping: Explore sub-locations within complex sites." },
    { icon: Star, text: "Feature Tagging System: Discover sites based on specific characteristics." }
  ];

  const communityFeatures = [
    { icon: Users, text: "Community Discussion System: Connect with other enthusiasts and researchers." },
    { icon: Camera, text: "Media Upload and Management: Share your knowledge, media, and experiences." },
    { icon: MagnifyingGlass, text: "Search and Filtering Capabilities: Find sites based on various criteria." },
    { icon: Lightbulb, text: "Admin Tools: Manage sites, media, and user contributions." }
  ];

  return (
    <Box
      bg={useColorModeValue('bg.default', 'bg.default')}
      py={{ base: 12, md: 16 }}
      position="relative" // Needed for pseudo-element
      _before={{ // Add background texture overlay
        content: '""',
        position: 'absolute',
        top: 0, left: 0, right: 0, bottom: 0,
        opacity: useColorModeValue(0.05, 0.08), // Subtle opacity for default background
        pointerEvents: 'none',
        bgImage: useColorModeValue('var(--texture-parchment)', 'var(--texture-leather)'),
        bgRepeat: 'repeat',
        bgSize: '500px',
        bgBlendMode: useColorModeValue('soft-light', 'multiply'),
        zIndex: 0,
      }}
    >
      <Container maxW="container.xl" position="relative" zIndex={1}> {/* Content needs higher z-index */}
        <VStack spacing={{ base: 12, md: 16 }}>
          {/* Enhanced Header Section */}
          <VStack spacing={6} textAlign="center">
            <HStack spacing={4} align="center">
              <Icon as={Star} boxSize={8} color={headingColor} />
              <Heading 
                as="h2" 
                size="2xl" 
                color={headingColor} 
                fontFamily="accent"
                letterSpacing="wide"
              >
                Key Features
              </Heading>
              <Icon as={Star} boxSize={8} color={headingColor} />
            </HStack>
            
            <Divider 
              orientation="horizontal" 
              borderColor={useColorModeValue('border.default', 'border.default')}
              opacity={0.6}
              w="300px"
            />
            
            <Text 
              fontSize={{ base: 'lg', md: 'xl' }} 
              maxW="4xl" 
              fontFamily="body" 
              color={textColorMuted}
              lineHeight="tall"
            >
              Discover the powerful tools and features that make Megalith Maps your ultimate 
              resource for exploring and documenting megalithic sites around the world.
            </Text>
          </VStack>

          {/* Enhanced Feature Cards Grid */}
          <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={8} width="full">
            {features.map((feature, index) => (
              <Card
                key={index}
                variant="journal"
                textAlign="center"
                transition="all 0.4s ease"
                _hover={{
                  transform: 'translateY(-8px) rotate(1deg)',
                  boxShadow: 'xl',
                  borderColor: feature.iconColor,
                }}
                border="2px solid"
                borderColor={useColorModeValue('border.light', 'border.light')}
                position="relative"
                overflow="hidden"
                bg={useColorModeValue('bg.surface', 'bg.surface')}
                _before={{
                  content: '""',
                  position: 'absolute',
                  top: 0, left: 0, right: 0, bottom: 0,
                  opacity: useColorModeValue(0.08, 0.12),
                  pointerEvents: 'none',
                  bgImage: 'var(--texture-aged)',
                  bgRepeat: 'repeat',
                  bgSize: '250px',
                  bgBlendMode: useColorModeValue('multiply', 'soft-light'),
                  zIndex: 0,
                  borderRadius: 'inherit',
                }}
                _after={{
                  content: '""',
                  position: 'absolute',
                  top: '10px',
                  right: '10px',
                  width: '30px',
                  height: '30px',
                  bgImage: 'var(--texture-compass)',
                  bgRepeat: 'no-repeat',
                  bgPosition: 'center',
                  bgSize: 'contain',
                  opacity: 0.2,
                  pointerEvents: 'none',
                  zIndex: 1,
                }}
              >
                <CardBody position="relative" zIndex={2} p={6}>
                  <VStack spacing={6}>
                    {/* Enhanced Icon with Badge */}
                    <Box position="relative">
                      <Flex
                        w="80px"
                        h="80px"
                        bg={useColorModeValue('bg.accent', 'bg.accent')}
                        color={feature.iconColor}
                        borderRadius="full"
                        justify="center"
                        align="center"
                        boxShadow="lg"
                        border="3px solid"
                        borderColor={useColorModeValue('border.default', 'border.default')}
                        position="relative"
                        _before={{
                          content: '""',
                          position: 'absolute',
                          inset: '-2px',
                          borderRadius: 'full',
                          background: `linear-gradient(45deg, ${feature.iconColor}, transparent, ${feature.iconColor})`,
                          zIndex: -1,
                          opacity: 0.3,
                        }}
                      >
                        <Icon as={feature.icon} weight="duotone" boxSize={10} />
                      </Flex>
                      
                      <Badge
                        position="absolute"
                        top="-5px"
                        right="-5px"
                        bg={feature.iconColor}
                        color="white"
                        borderRadius="full"
                        fontSize="xs"
                        px={2}
                        py={1}
                        fontFamily="handwritten"
                        transform="rotate(15deg)"
                      >
                        {index + 1}
                      </Badge>
                    </Box>

                    <VStack spacing={3}>
                      <Heading 
                        size="md" 
                        fontFamily="accent" 
                        color={textColor}
                        letterSpacing="wide"
                      >
                        {feature.title}
                      </Heading>
                      
                      <Text 
                        color={textColorMuted} 
                        fontFamily="body"
                        fontSize="sm"
                        lineHeight="tall"
                      >
                        {feature.description}
                      </Text>
                    </VStack>
                  </VStack>
                </CardBody>
              </Card>
            ))}
          </SimpleGrid>

          {/* Platform & Community Features Lists */}
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={8} width="full" pt={8}>
            <Card
              variant="journal"
              position="relative" // Needed for pseudo-element
              overflow="hidden" // Keep texture contained
              _before={{ // Add internal texture
                content: '""',
                position: 'absolute',
                top: 0, left: 0, right: 0, bottom: 0,
                opacity: useColorModeValue(0.06, 0.1),
                pointerEvents: 'none',
                bgImage: 'var(--texture-aged)',
                bgRepeat: 'repeat',
                bgSize: '300px',
                bgBlendMode: useColorModeValue('multiply', 'soft-light'),
                zIndex: 0,
                borderRadius: 'inherit',
              }}
            >
              <CardHeader position="relative" zIndex={1}> {/* Ensure content is above texture */}
                <Heading as="h3" size="lg" color={headingColor} fontFamily="heading">
                  Platform Features
                </Heading>
              </CardHeader>
              <CardBody>
                <List spacing={4} fontSize="md">
                  {platformFeatures.map((item, index) => (
                    <ListItem key={index}>
                      <HStack spacing={3} align="center">
                        <Icon as={item.icon} boxSize={5} color={accentBorderColor} weight="duotone" />
                        <Text fontFamily="body" color={textColor}>{item.text}</Text>
                      </HStack>
                    </ListItem>
                  ))}
                </List>
              </CardBody>
            </Card>

            <Card
              variant="journal"
              position="relative" // Needed for pseudo-element
              overflow="hidden" // Keep texture contained
              _before={{ // Add internal texture
                content: '""',
                position: 'absolute',
                top: 0, left: 0, right: 0, bottom: 0,
                opacity: useColorModeValue(0.06, 0.1),
                pointerEvents: 'none',
                bgImage: 'var(--texture-aged)',
                bgRepeat: 'repeat',
                bgSize: '300px',
                bgBlendMode: useColorModeValue('multiply', 'soft-light'),
                zIndex: 0,
                borderRadius: 'inherit',
              }}
            >
              <CardHeader position="relative" zIndex={1}> {/* Ensure content is above texture */}
                <Heading as="h3" size="lg" color={headingColor} fontFamily="heading">
                  Community Features
                </Heading>
              </CardHeader>
              <CardBody>
                <List spacing={4} fontSize="md">
                  {communityFeatures.map((item, index) => (
                    <ListItem key={index}>
                      <HStack spacing={3} align="center">
                        <Icon as={item.icon} boxSize={5} color={accentBorderColor} weight="duotone" />
                        <Text fontFamily="body" color={textColor}>{item.text}</Text>
                      </HStack>
                    </ListItem>
                  ))}
                </List>
              </CardBody>
            </Card>
          </SimpleGrid>
        </VStack>
      </Container>
    </Box>
  );
};